<template>
  <div class="instalaciones-container">
    <!-- Gestión de Instalaciones -->
    <v-card>
      <v-card-title>Gestión de Instalaciones</v-card-title>
      <v-card-text>
        <v-tabs v-model="activeTab" class="mb-4">
          <v-tab value="bodegas">Bodegas</v-tab>
          <v-tab value="zonas">Zonas</v-tab>
          <v-tab value="ubicaciones">Ubicaciones</v-tab>
        </v-tabs>

        <v-tabs-window v-model="activeTab">
          <!-- Tab de Bodegas -->
          <v-tabs-window-item value="bodegas">
            <div class="d-flex justify-space-between align-center flex-wrap mb-4">
              <h6 class="text-h6 mb-2 mb-md-0">Listado de Bodegas</h6>
              <v-btn
                color="primary"
                prepend-icon="mdi-plus"
                @click="addWarehouse"
                size="default"
              >
                <span class="d-none d-sm-inline">Nueva Bodega</span>
                <span class="d-sm-none">Nueva</span>
              </v-btn>
            </div>
            
            <!-- Tabla de Bodegas -->
            <ResponsiveDataTable
              :items="warehouses"
              :headers="[
                { title: 'Código', key: 'code', align: 'start', sortable: true },
                { title: 'Nombre', key: 'name', align: 'start', sortable: true },
                { title: 'Dirección', key: 'address', align: 'start', sortable: true },
                { title: 'Descripción', key: 'description', align: 'start', sortable: false },
                { title: 'Estado', key: 'status', align: 'center', sortable: true },
                { title: 'Acciones', key: 'actions', align: 'center', sortable: false }
              ]"
              class="elevation-1 warehouses-table"
              :show-select="false"
              items-per-page="10"
              density="default"
              hover
            >
              <template v-slot:item.description="{ item }">
                {{ item.description || 'Sin descripción' }}
              </template>

              <template v-slot:item.status="{ item }">
                <v-chip
                  :color="item.status === 'Activo' ? 'success' : 'error'"
                  text-color="white"
                  size="small"
                >
                  {{ item.status }}
                </v-chip>
              </template>

              <template v-slot:item.actions="{ item }">
                <v-btn
                  icon="mdi-earth-box"
                  size="small"
                  variant="text"
                  color="teal"
                  @click="handleGln('warehouse', item)"
                  :title="item.gln ? 'Imprimir GLN ' + item.gln : 'Asignar e imprimir GLN (414)'"
                ></v-btn>
                <v-btn
                  icon="mdi-grid"
                  size="small"
                  variant="text"
                  color="info"
                  @click="viewWarehouseZones(item)"
                ></v-btn>
                <v-btn
                  icon="mdi-pencil"
                  size="small"
                  variant="text"
                  color="primary"
                  @click="editWarehouse(item)"
                ></v-btn>
                <v-btn
                  icon="mdi-delete"
                  size="small"
                  variant="text"
                  color="error"
                  @click="deleteWarehouse(item)"
                ></v-btn>
              </template>

              <template v-slot:no-data>
                <div class="text-center py-4">
                  <v-icon icon="mdi-warehouse" color="grey" size="large"></v-icon>
                  <p class="text-subtitle-1 mt-2">No hay bodegas registradas</p>
                </div>
              </template>
            </ResponsiveDataTable>
          </v-tabs-window-item>
          
          <!-- Tab de Zonas -->
          <v-tabs-window-item value="zonas">
            <v-row class="mb-4">
              <v-col cols="12" md="6">
                <v-select
                  v-model="selectedWarehouseId"
                  :items="warehouses"
                  item-title="name"
                  item-value="id"
                  label="Seleccionar Bodega"
                  @update:model-value="loadWarehouseZones"
                  clearable
                ></v-select>
              </v-col>
            </v-row>

            <div v-if="selectedWarehouse">
              <div class="d-flex justify-space-between align-center flex-wrap mb-4">
                <h6 class="text-h6 mb-2 mb-md-0">Zonas de la Bodega: {{ selectedWarehouse.name }}</h6>
                <v-btn
                  color="primary"
                  prepend-icon="mdi-plus"
                  @click="addZone(selectedWarehouse.id)"
                  size="default"
                >
                  <span class="d-none d-sm-inline">Nueva Zona</span>
                  <span class="d-sm-none">Nueva</span>
                </v-btn>
              </div>
              
              <!-- Tabla de Zonas -->
              <ResponsiveDataTable
                :items="zones"
                :headers="[
                  { title: 'Código', key: 'code', align: 'start', sortable: true },
                  { title: 'Nombre', key: 'name', align: 'start', sortable: true },
                  { title: 'Descripción', key: 'description', align: 'start', sortable: false },
                  { title: 'Peso', key: 'weight', align: 'end', sortable: true },
                  { title: 'Ubicaciones', key: 'location_count', align: 'center', sortable: true },
                  { title: 'Acciones', key: 'actions', align: 'center', sortable: false }
                ]"
                class="elevation-1 zones-table"
                :show-select="false"
                items-per-page="10"
                density="default"
                hover
              >
                <template v-slot:item.description="{ item }">
                  {{ item.description || 'Sin descripción' }}
                </template>

                <template v-slot:item.weight="{ item }">
                  {{ item.weight || 0 }}
                </template>

                <template v-slot:item.location_count="{ item }">
                  <v-chip color="info" size="small">
                    {{ getLocationCountForZone(item.id) }}
                  </v-chip>
                </template>

                <template v-slot:item.actions="{ item }">
                  <v-btn
                    icon="mdi-earth-box"
                    size="small"
                    variant="text"
                    color="teal"
                    @click="handleGln('zone', item)"
                    :title="item.gln ? 'Imprimir GLN ' + item.gln : 'Asignar e imprimir GLN (414)'"
                  ></v-btn>
                  <v-btn
                    icon="mdi-map-marker-multiple"
                    size="small"
                    variant="text"
                    color="info"
                    @click="viewZoneLocations(item)"
                    :title="'Ver ubicaciones de ' + item.name"
                  ></v-btn>
                  <v-btn
                    icon="mdi-pencil"
                    size="small"
                    variant="text"
                    color="primary"
                    @click="editZone(item)"
                  ></v-btn>
                  <v-btn
                    icon="mdi-delete"
                    size="small"
                    variant="text"
                    color="error"
                    @click="deleteZone(item)"
                  ></v-btn>
                </template>

                <template v-slot:no-data>
                  <div class="text-center py-4">
                    <v-icon icon="mdi-map-marker" color="grey" size="large"></v-icon>
                    <p class="text-subtitle-1 mt-2">No hay zonas registradas para esta bodega</p>
                  </div>
                </template>
              </ResponsiveDataTable>
            </div>
            
            <div v-else class="text-center py-8">
              <v-icon icon="mdi-map-search" color="grey" size="x-large"></v-icon>
              <p class="text-h6 mt-4">Seleccione una bodega para ver sus zonas</p>
            </div>
          </v-tabs-window-item>

          <!-- Tab de Ubicaciones -->
          <v-tabs-window-item value="ubicaciones">
            <v-row class="mb-4">
              <v-col cols="12" md="4">
                <v-select
                  v-model="selectedWarehouseIdForLocations"
                  :items="warehouses"
                  item-title="name"
                  item-value="id"
                  label="Seleccionar Bodega"
                  @update:model-value="loadWarehouseZonesForLocations"
                  clearable
                ></v-select>
              </v-col>
              <v-col cols="12" md="4">
                <v-select
                  v-model="selectedZoneIdForLocations"
                  :items="zonesForLocations"
                  item-title="name"
                  item-value="id"
                  label="Seleccionar Zona"
                  @update:model-value="loadZoneLocations"
                  clearable
                  :disabled="!selectedWarehouseIdForLocations"
                ></v-select>
              </v-col>
            </v-row>

            <div v-if="selectedZoneForLocations">
              <div class="locations-header mb-4">
                <div class="locations-title">
                  <h6 class="text-h6">
                    Ubicaciones de la Zona: {{ selectedZoneForLocations.name }}
                    <v-chip color="info" size="small" class="ml-2">
                      {{ locations.length }} ubicaciones
                    </v-chip>
                  </h6>
                </div>
                <div class="locations-actions">
                  <v-btn
                    icon="mdi-cog"
                    color="grey"
                    variant="text"
                    @click="showConfigDialog = true"
                    :title="'Configuración de ubicaciones'"
                    size="default"
                  ></v-btn>
                  <!-- Botón Generar Códigos comentado - ya no es necesario ya que los códigos se generan automáticamente al crear ubicaciones
                  <v-btn
                    color="warning"
                    prepend-icon="mdi-barcode"
                    @click="generateBarcodes"
                    :loading="generatingBarcodes"
                    variant="outlined"
                    size="default"
                  >
                    <span class="d-none d-md-inline">Generar Códigos</span>
                    <span class="d-md-none">Generar</span>
                  </v-btn>
                  -->
                  <v-btn
                    color="success"
                    prepend-icon="mdi-printer"
                    @click="showPrintDialog = true"
                    :disabled="selectedLocations.length === 0"
                    size="default"
                  >
                    <span class="d-none d-lg-inline">Imprimir Códigos ({{ selectedLocations.length }})</span>
                    <span class="d-none d-md-inline d-lg-none">Imprimir ({{ selectedLocations.length }})</span>
                    <span class="d-md-none">Imprimir</span>
                  </v-btn>
                  <v-btn
                    color="primary"
                    prepend-icon="mdi-plus"
                    @click="addLocation(selectedZoneForLocations.id)"
                    size="default"
                  >
                    <span class="d-none d-sm-inline">Nueva Ubicación</span>
                    <span class="d-sm-none">Nueva</span>
                  </v-btn>
                </div>
              </div>
              
              <!-- Tabla de Ubicaciones -->
              <ResponsiveDataTable
                v-model="selectedLocations"
                :items="locations"
                :headers="[
                  { title: 'Código', key: 'code', align: 'start', sortable: true },
                  { title: 'Descripción', key: 'description', align: 'start', sortable: false },
                  { title: 'Código de Barras', key: 'barcode', align: 'start', sortable: true },
                  { title: 'Coordenadas', key: 'coordinates', align: 'start', sortable: false },
                  { title: 'Capacidad', key: 'capacity', align: 'center', sortable: true },
                  { title: 'Ocupación', key: 'occupancy', align: 'center', sortable: true },
                  { title: 'Acciones', key: 'actions', align: 'center', sortable: false }
                ]"
                class="elevation-1 locations-table"
                show-select
                items-per-page="15"
                density="default"
                hover
                item-value="id"
              >
                <template v-slot:item.barcode="{ item }">
                  <div class="d-flex align-center">
                    <code class="mr-2">{{ item.barcode || 'Sin código' }}</code>
                    <v-btn
                      v-if="item.barcode"
                      icon="mdi-qrcode"
                      size="x-small"
                      variant="text"
                      color="primary"
                      @click="showBarcodePreview(item)"
                      :title="'Ver código de barras de ' + item.code"
                    ></v-btn>
                  </div>
                </template>

                <template v-slot:item.coordinates="{ item }">
                  <div class="text-caption">
                    <div v-if="item.aisle">Isla: {{ item.aisle }}</div>
                    <div v-if="item.rack">Rack: {{ item.rack }}</div>
                    <div v-if="item.level">Nivel: {{ item.level }}</div>
                    <div v-if="item.position">Pos: {{ item.position }}</div>
                  </div>
                </template>

                <template v-slot:item.capacity="{ item }">
                  <div v-if="item.is_volumetric" class="text-center">
                    <v-chip color="info" size="small" class="mb-1">
                      <v-icon icon="mdi-cube-outline" size="small" class="mr-1"></v-icon>
                      Volumétrica
                    </v-chip>
                    <div class="text-caption">
                      {{ Math.round(item.volumetric_capacity / 1000000 * 1000) / 1000 }} m³
                    </div>
                    <div class="text-caption text-grey">
                      {{ item.volumetric_capacity?.toLocaleString() }} cm³
                    </div>
                  </div>
                  <div v-else class="text-center">
                    <v-chip color="grey" size="small" class="mb-1">
                      <v-icon icon="mdi-package-variant" size="small" class="mr-1"></v-icon>
                      Tradicional
                    </v-chip>
                    <div class="text-caption">
                      {{ item.capacity || 0 }} unidades
                    </div>
                  </div>
                </template>

                <template v-slot:item.occupancy="{ item }">
                  <v-progress-linear
                    :model-value="getLocationOccupancy(item)"
                    :color="getOccupancyColor(getLocationOccupancy(item))"
                    height="8"
                    class="mb-1"
                  ></v-progress-linear>
                  <div class="text-caption text-center">
                    {{ Math.round(getLocationOccupancy(item)) }}%
                  </div>
                </template>

                <template v-slot:item.actions="{ item }">
                  <v-btn
                    icon="mdi-earth-box"
                    size="small"
                    variant="text"
                    color="teal"
                    @click="handleGln('location', item)"
                    :title="item.gln ? 'Imprimir GLN ' + item.gln : 'Asignar e imprimir GLN (414)'"
                  ></v-btn>
                  <v-btn
                    icon="mdi-pencil"
                    size="small"
                    variant="text"
                    color="primary"
                    @click="editLocation(item)"
                  ></v-btn>
                  <v-btn
                    icon="mdi-delete"
                    size="small"
                    variant="text"
                    color="error"
                    @click="deleteLocation(item)"
                  ></v-btn>
                </template>

                <template v-slot:no-data>
                  <div class="text-center py-4">
                    <v-icon icon="mdi-map-marker-off" color="grey" size="large"></v-icon>
                    <p class="text-subtitle-1 mt-2">No hay ubicaciones registradas para esta zona</p>
                  </div>
                </template>
              </ResponsiveDataTable>
            </div>
            
            <div v-else class="text-center py-8">
              <v-icon icon="mdi-map-search-outline" color="grey" size="x-large"></v-icon>
              <p class="text-h6 mt-4">Seleccione una bodega y zona para ver las ubicaciones</p>
            </div>
          </v-tabs-window-item>
        </v-tabs-window>
      </v-card-text>
    </v-card>
    
    <!-- Modal de Nueva/Editar Bodega -->
    <v-dialog v-model="showWarehouseDialog" max-width="600">
      <v-card>
        <v-card-title>{{ isEditingWarehouse ? 'Editar Bodega' : 'Nueva Bodega' }}</v-card-title>
        <v-card-text>
          <v-form ref="warehouseForm">
            <v-text-field
              v-model="warehouseFormData.code"
              label="Código"
              :disabled="isEditingWarehouse"
              :rules="[v => !!v || 'El código es requerido']"
            ></v-text-field>
            
            <v-text-field
              v-model="warehouseFormData.name"
              label="Nombre"
              :rules="[v => !!v || 'El nombre es requerido']"
            ></v-text-field>
            
            <v-text-field
              v-model="warehouseFormData.address"
              label="Dirección"
            ></v-text-field>
            
            <v-textarea
              v-model="warehouseFormData.description"
              label="Descripción"
              rows="3"
            ></v-textarea>
            
            <v-select
              v-model="warehouseFormData.status"
              :items="[
                { title: 'Activo', value: 'Activo' },
                { title: 'Inactivo', value: 'Inactivo' }
              ]"
              label="Estado"
            ></v-select>
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            color="grey"
            variant="text"
            @click="showWarehouseDialog = false"
          >
            Cancelar
          </v-btn>
          <v-btn
            color="primary"
            @click="saveWarehouse"
            :disabled="!isWarehouseFormValid"
          >
            Guardar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    
    <!-- Modal de Nueva/Editar Zona -->
    <v-dialog v-model="showZoneDialog" max-width="800">
      <v-card>
        <v-card-title>{{ isEditingZone ? 'Editar Zona' : 'Nueva Zona' }}</v-card-title>
        <v-card-text>
          <v-form ref="zoneForm">
            <v-text-field
              v-model="zoneFormData.code"
              label="Código"
              :disabled="isEditingZone"
              :rules="[v => !!v || 'El código es requerido']"
            ></v-text-field>
            
            <v-text-field
              v-model="zoneFormData.name"
              label="Nombre"
              :rules="[v => !!v || 'El nombre es requerido']"
            ></v-text-field>
            
            <v-textarea
              v-model="zoneFormData.description"
              label="Descripción"
              rows="3"
            ></v-textarea>
            
            <v-row>
              <v-col cols="12" md="3">
                <v-text-field
                  v-model="zoneFormData.aisle"
                  label="Isla"
                  placeholder="Ej: A1"
                ></v-text-field>
              </v-col>
              <v-col cols="12" md="3">
                <v-text-field
                  v-model="zoneFormData.rack"
                  label="Rack"
                  placeholder="Ej: R1"
                ></v-text-field>
              </v-col>
              <v-col cols="12" md="3">
                <v-text-field
                  v-model="zoneFormData.level"
                  label="Nivel"
                  placeholder="Ej: L1"
                ></v-text-field>
              </v-col>
              <v-col cols="12" md="3">
                <v-text-field
                  v-model="zoneFormData.position"
                  label="Posición"
                  placeholder="Ej: P1"
                ></v-text-field>
              </v-col>
            </v-row>

            <v-text-field
              v-model.number="zoneFormData.weight"
              type="number"
              label="Peso (para cálculo de ruta más corta)"
              min="1"
              step="1"
              :rules="[v => v > 0 || 'El peso debe ser mayor a 0']"
              hint="Mayor peso indica mayor distancia o dificultad de acceso"
              persistent-hint
            ></v-text-field>

            <v-text-field
              v-model.number="zoneFormData.capacity"
              type="number"
              label="Capacidad máxima"
              min="1"
              step="1"
              :rules="[v => v > 0 || 'La capacidad debe ser mayor a 0']"
              hint="Cantidad máxima de productos que admite"
              persistent-hint
            ></v-text-field>
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            color="grey"
            variant="text"
            @click="showZoneDialog = false"
          >
            Cancelar
          </v-btn>
          <v-btn
            color="primary"
            @click="saveZone"
            :disabled="!isZoneFormValid"
          >
            Guardar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Modal de Nueva/Editar Ubicación -->
    <v-dialog v-model="showLocationDialog" max-width="800">
      <v-card>
        <v-card-title>{{ isEditingLocation ? 'Editar Ubicación' : 'Nueva Ubicación' }}</v-card-title>
        <v-card-text>
          <v-form ref="locationForm">
            <v-text-field
              v-model="locationFormData.code"
              label="Código"
              :disabled="isEditingLocation"
              :rules="[v => !!v || 'El código es requerido']"
              hint="Ej: A010101, B020202"
              persistent-hint
            ></v-text-field>
            
            <v-textarea
              v-model="locationFormData.description"
              label="Descripción"
              rows="2"
            ></v-textarea>
            
            <v-select
              v-model="locationFormData.location_type"
              :items="[
                { title: 'Almacenamiento', value: 'storage' },
                { title: 'Picking', value: 'picking' },
                { title: 'Recepción', value: 'receiving' },
                { title: 'Despacho', value: 'shipping' },
                { title: 'Control de Calidad', value: 'quality_control' },
                { title: 'Devoluciones', value: 'returns' }
              ]"
              label="Tipo de Ubicación"
              :rules="[v => !!v || 'El tipo de ubicación es requerido']"
            ></v-select>
            
            <v-row>
              <v-col cols="12" md="3">
                <v-text-field
                  v-model="locationFormData.aisle"
                  label="Isla"
                  placeholder="Ej: A01"
                ></v-text-field>
              </v-col>
              <v-col cols="12" md="3">
                <v-text-field
                  v-model="locationFormData.rack"
                  label="Rack"
                  placeholder="Ej: R01"
                ></v-text-field>
              </v-col>
              <v-col cols="12" md="3">
                <v-text-field
                  v-model="locationFormData.level"
                  label="Nivel"
                  placeholder="Ej: L01"
                ></v-text-field>
              </v-col>
              <v-col cols="12" md="3">
                <v-text-field
                  v-model="locationFormData.position"
                  label="Posición"
                  placeholder="Ej: P01"
                ></v-text-field>
              </v-col>
            </v-row>

            <!-- Campo de capacidad tradicional (cuando volumétrica está deshabilitada) -->
            <v-row v-if="!locationConfig.volumetricCapacity.enabled">
              <v-col cols="12" md="6">
                <v-text-field
                  v-model.number="locationFormData.capacity"
                  type="number"
                  label="Capacidad máxima"
                  min="1"
                  step="1"
                  :rules="[v => v > 0 || 'La capacidad debe ser mayor a 0']"
                  hint="Cantidad máxima de productos que admite (en unidades)"
                  persistent-hint
                ></v-text-field>
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model.number="locationFormData.weight"
                  type="number"
                  label="Peso (para cálculo de ruta)"
                  min="1"
                  step="1"
                  :rules="[v => v > 0 || 'El peso debe ser mayor a 0']"
                  hint="Mayor peso indica mayor distancia o dificultad de acceso"
                  persistent-hint
                ></v-text-field>
              </v-col>
            </v-row>

            <!-- Campos de capacidad volumétrica -->
            <v-row v-if="locationConfig.volumetricCapacity.enabled">
              <v-col cols="12">
                <v-alert type="info" variant="tonal" class="mb-3">
                  <div class="text-body-2">
                    <strong>Capacidad Volumétrica Habilitada:</strong>
                    Ingrese las dimensiones de la ubicación en centímetros. La capacidad se calculará automáticamente.
                  </div>
                </v-alert>
              </v-col>
              <v-col cols="12" md="4">
                <v-text-field
                  v-model.number="locationFormData.width"
                  type="number"
                  label="Ancho (cm)"
                  min="1"
                  step="0.1"
                  :rules="locationConfig.volumetricCapacity.enabled ? [v => v > 0 || 'El ancho es requerido'] : []"
                  hint="Ancho de la ubicación en centímetros"
                  persistent-hint
                ></v-text-field>
              </v-col>
              <v-col cols="12" md="4">
                <v-text-field
                  v-model.number="locationFormData.length"
                  type="number"
                  label="Largo (cm)"
                  min="1"
                  step="0.1"
                  :rules="locationConfig.volumetricCapacity.enabled ? [v => v > 0 || 'El largo es requerido'] : []"
                  hint="Largo de la ubicación en centímetros"
                  persistent-hint
                ></v-text-field>
              </v-col>
              <v-col cols="12" md="4">
                <v-text-field
                  v-model.number="locationFormData.height"
                  type="number"
                  label="Alto (cm)"
                  min="1"
                  step="0.1"
                  :rules="locationConfig.volumetricCapacity.enabled ? [v => v > 0 || 'El alto es requerido'] : []"
                  hint="Alto de la ubicación en centímetros"
                  persistent-hint
                ></v-text-field>
              </v-col>
              <v-col cols="12" v-if="locationFormData.width && locationFormData.length && locationFormData.height">
                <v-card variant="outlined">
                  <v-card-text>
                    <div class="text-subtitle-2 mb-2">Capacidad Volumétrica Calculada:</div>
                    <div class="text-h6 text-primary">
                      {{ calculateVolumetricCapacity() }} cm³
                    </div>
                    <div class="text-caption text-grey">
                      {{ (calculateVolumetricCapacity() / 1000000).toFixed(3) }} m³
                    </div>
                  </v-card-text>
                </v-card>
              </v-col>
            </v-row>

            <!-- Mostrar código de barras si existe -->
            <v-card v-if="locationFormData.barcode" variant="outlined" class="mt-4">
              <v-card-subtitle>Código de Barras Generado</v-card-subtitle>
              <v-card-text class="text-center">
                <div class="barcode-display">
                  <canvas ref="barcodeCanvas" class="barcode-canvas"></canvas>
                </div>
                <div class="mt-2">
                  <code>{{ locationFormData.barcode }}</code>
                </div>
              </v-card-text>
            </v-card>
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            color="grey"
            variant="text"
            @click="showLocationDialog = false"
          >
            Cancelar
          </v-btn>
          <v-btn
            color="primary"
            @click="saveLocation"
            :disabled="!isLocationFormValid"
          >
            Guardar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Modal de Vista Previa de Código de Barras -->
    <v-dialog v-model="showBarcodePreviewDialog" max-width="500">
      <v-card v-if="selectedLocationForBarcode">
        <v-card-title>
          Código de Barras - {{ selectedLocationForBarcode.code }}
        </v-card-title>
        <v-card-text class="text-center">
          <div class="barcode-display">
            <canvas ref="previewBarcodeCanvas" class="barcode-canvas"></canvas>
          </div>
          <div class="mt-3">
            <code class="text-h6">{{ selectedLocationForBarcode.barcode }}</code>
          </div>
          <div class="mt-2 text-caption text-grey">
            {{ selectedLocationForBarcode.description }}
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            color="grey"
            variant="text"
            @click="showBarcodePreviewDialog = false"
          >
            Cerrar
          </v-btn>
          <v-btn
            color="primary"
            @click="printSingleBarcode(selectedLocationForBarcode)"
            prepend-icon="mdi-printer"
          >
            Imprimir
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Modal de Impresión de Códigos -->
    <v-dialog v-model="showPrintDialog" max-width="600">
      <v-card>
        <v-card-title>
          Imprimir Códigos de Barras
          <v-chip color="info" size="small" class="ml-2">
            {{ selectedLocations.length }} ubicaciones seleccionadas
          </v-chip>
        </v-card-title>
        <v-card-text>
          <v-row>
            <v-col cols="12">
              <v-select
                v-model="printSettings.printerType"
                :items="[
                  { title: 'Impresora Estándar (A4)', value: 'standard' },
                  { title: 'Impresora de Etiquetas (Zebra/Térmica)', value: 'label' }
                ]"
                label="Tipo de Impresora"
              ></v-select>
            </v-col>
          </v-row>

          <v-row v-if="printSettings.printerType === 'standard'">
            <v-col cols="12" md="6">
              <v-select
                v-model="printSettings.labelsPerRow"
                :items="[1, 2, 3, 4]"
                label="Etiquetas por fila"
              ></v-select>
            </v-col>
            <v-col cols="12" md="6">
              <v-select
                v-model="printSettings.labelSize"
                :items="[
                  { title: 'Pequeño (2x1 cm)', value: 'small' },
                  { title: 'Mediano (4x2 cm)', value: 'medium' },
                  { title: 'Grande (6x3 cm)', value: 'large' }
                ]"
                label="Tamaño de etiqueta"
              ></v-select>
            </v-col>
          </v-row>

          <v-row v-if="printSettings.printerType === 'label'">
            <v-col cols="12" md="6">
              <v-select
                v-model="printSettings.labelFormat"
                :items="[
                  { title: '2x1 pulgadas', value: '2x1' },
                  { title: '4x2 pulgadas', value: '4x2' },
                  { title: '4x6 pulgadas', value: '4x6' }
                ]"
                label="Formato de etiqueta"
              ></v-select>
            </v-col>
            <v-col cols="12" md="6">
              <v-text-field
                v-model.number="printSettings.dpi"
                type="number"
                label="DPI"
                :value="203"
                hint="Resolución de la impresora (203, 300, 600)"
              ></v-text-field>
            </v-col>
          </v-row>

          <v-divider class="my-4"></v-divider>

          <div class="text-subtitle-2 mb-2">Vista Previa:</div>
          <div class="print-preview" :class="printSettings.printerType">
            <div 
              v-for="(location, index) in selectedLocationsForPrint" 
              :key="location.id"
              class="barcode-label"
              :class="printSettings.labelSize"
            >
              <div class="barcode-code">{{ location.code }}</div>
              <div class="barcode-visual">
                <canvas 
                  :id="`previewCanvas${index}`"
                  class="preview-barcode-canvas"
                ></canvas>
              </div>
              <div class="barcode-text">{{ location.barcode }}</div>
            </div>
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            color="grey"
            variant="text"
            @click="showPrintDialog = false"
          >
            Cancelar
          </v-btn>
          <v-btn
            color="primary"
            @click="printSelectedBarcodes"
            prepend-icon="mdi-printer"
          >
            Imprimir
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    
    <!-- Modal de Confirmación de Eliminación -->
    <v-dialog v-model="showDeleteDialog" max-width="400">
      <v-card>
        <v-card-title>Confirmar Eliminación</v-card-title>
        <v-card-text>
          {{ deleteConfirmMessage }}
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            color="grey"
            variant="text"
            @click="showDeleteDialog = false"
          >
            Cancelar
          </v-btn>
          <v-btn
            color="error"
            @click="confirmDelete"
          >
            Eliminar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    
    <!-- Modal de Configuración de Ubicaciones -->
    <v-dialog v-model="showConfigDialog" max-width="600">
      <v-card>
        <v-card-title>
          <v-icon icon="mdi-cog" class="mr-2"></v-icon>
          Configuración de Ubicaciones
        </v-card-title>
        <v-card-text>
          <v-form ref="configForm">
            <!-- Configuración de colores de ocupación -->
            <v-card variant="outlined" class="mb-4">
              <v-card-subtitle>Colores de Ocupación</v-card-subtitle>
              <v-card-text>
                <v-row>
                  <v-col cols="12" md="4">
                    <v-text-field
                      v-model.number="locationConfig.occupancyColors.green.max"
                      type="number"
                      label="Verde hasta (%)"
                      min="0"
                      max="100"
                      :rules="[v => v >= 0 && v <= 100 || 'Debe estar entre 0 y 100']"
                      hint="Ocupación baja (verde)"
                      persistent-hint
                    >
                      <template v-slot:prepend>
                        <v-icon color="success">mdi-circle</v-icon>
                      </template>
                    </v-text-field>
                  </v-col>
                  <v-col cols="12" md="4">
                    <v-text-field
                      v-model.number="locationConfig.occupancyColors.yellow.max"
                      type="number"
                      label="Amarillo hasta (%)"
                      min="0"
                      max="100"
                      :rules="[v => v >= 0 && v <= 100 || 'Debe estar entre 0 y 100']"
                      hint="Ocupación media (amarillo)"
                      persistent-hint
                    >
                      <template v-slot:prepend>
                        <v-icon color="warning">mdi-circle</v-icon>
                      </template>
                    </v-text-field>
                  </v-col>
                  <v-col cols="12" md="4">
                    <v-text-field
                      value="100"
                      label="Rojo hasta (%)"
                      disabled
                      hint="Ocupación alta (rojo)"
                      persistent-hint
                    >
                      <template v-slot:prepend>
                        <v-icon color="error">mdi-circle</v-icon>
                      </template>
                    </v-text-field>
                  </v-col>
                </v-row>
                
                <!-- Vista previa de colores -->
                <div class="mt-4">
                  <v-card variant="outlined">
                    <v-card-subtitle>Vista Previa</v-card-subtitle>
                    <v-card-text>
                      <div class="d-flex align-center mb-2">
                        <span class="mr-2" style="width: 60px;">30%:</span>
                        <v-progress-linear
                          :model-value="30"
                          :color="getOccupancyColorPreview(30)"
                          height="8"
                          class="flex-grow-1"
                        ></v-progress-linear>
                      </div>
                      <div class="d-flex align-center mb-2">
                        <span class="mr-2" style="width: 60px;">70%:</span>
                        <v-progress-linear
                          :model-value="70"
                          :color="getOccupancyColorPreview(70)"
                          height="8"
                          class="flex-grow-1"
                        ></v-progress-linear>
                      </div>
                      <div class="d-flex align-center">
                        <span class="mr-2" style="width: 60px;">90%:</span>
                        <v-progress-linear
                          :model-value="90"
                          :color="getOccupancyColorPreview(90)"
                          height="8"
                          class="flex-grow-1"
                        ></v-progress-linear>
                      </div>
                    </v-card-text>
                  </v-card>
                </div>
              </v-card-text>
            </v-card>

            <!-- Configuración de capacidad volumétrica -->
            <v-card variant="outlined">
              <v-card-subtitle>Capacidad Volumétrica</v-card-subtitle>
              <v-card-text>
                <v-switch
                  v-model="locationConfig.volumetricCapacity.enabled"
                  label="Habilitar capacidad volumétrica"
                  color="primary"
                  inset
                ></v-switch>
                
                <v-alert
                  v-if="locationConfig.volumetricCapacity.enabled"
                  type="info"
                  variant="tonal"
                  class="mt-3"
                >
                  <div class="text-body-2">
                    <strong>Capacidad Volumétrica Habilitada:</strong>
                    <ul class="mt-2">
                      <li>Las ubicaciones requerirán dimensiones (ancho, largo, alto)</li>
                      <li>La capacidad se calculará automáticamente basada en el volumen</li>
                      <li>Los productos deben tener dimensiones obligatorias</li>
                      <li>La ocupación se calculará por volumen ocupado vs. disponible</li>
                    </ul>
                  </div>
                </v-alert>
                
                <v-alert
                  v-else
                  type="warning"
                  variant="tonal"
                  class="mt-3"
                >
                  <div class="text-body-2">
                    <strong>Capacidad Manual:</strong>
                    <br>
                    Se utilizará la capacidad máxima ingresada manualmente para cada ubicación.
                  </div>
                </v-alert>
              </v-card-text>
            </v-card>
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            color="grey"
            variant="text"
            @click="cancelConfig"
          >
            Cancelar
          </v-btn>
          <v-btn
            color="primary"
            @click="saveConfig"
          >
            Guardar Configuración
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script setup>
import { ref, computed, watch, nextTick } from 'vue'
import { useStore } from 'vuex'
import api from '@/api'
import JsBarcode from 'jsbarcode'
import { renderGS1128DataURL } from '@/utils/gs1Barcode'
import ResponsiveDataTable from '@/components/ResponsiveDataTable.vue'
import '@fortawesome/fontawesome-free/css/all.css'
import 'bootstrap-icons/font/bootstrap-icons.css'

// Store
const store = useStore()

// GLN (AI 414) por bodega/zona/ubicación (checklist GS1: crear y gestionar
// GLN, no solo leerlos). Solo con prefijo GS1 configurado — el backend
// responde 422 con el motivo si falta. Idempotente: re-imprimir no consume
// correlativo.
const handleGln = async (entityType, item) => {
  try {
    let gln = item.gln
    if (!gln) {
      const { data } = await api.assignGln(entityType, item.id)
      gln = data.gln
      item.gln = gln
      store.dispatch('showSuccess', `GLN asignado: ${gln}`)
    }
    const names = { warehouse: 'Bodega', zone: 'Zona', location: 'Ubicación' }
    const img = renderGS1128DataURL(`(414)${gln}`, { scale: 2, height: 14 })
    const w = window.open('', '_blank', 'width=480,height=440')
    w.document.write(`<!DOCTYPE html><html><head><title>GLN ${gln}</title>
      <style>body{font-family:Arial,sans-serif;text-align:center;padding:18px}h3{margin:4px 0}h2{margin:10px 0 6px;letter-spacing:1px}</style>
      </head><body>
      <h3>${names[entityType]}: ${item.name || item.code || ''}</h3>
      <div>${item.code || ''}</div>
      <h2>GLN (414): ${gln}</h2>
      <img src="${img}" style="max-width:100%"/>
      <script>window.onload = () => setTimeout(() => window.print(), 300)<\/script>
      </body></html>`)
    w.document.close()
  } catch (e) {
    console.error('Error con GLN:', e)
    store.dispatch('showError', e.response?.data?.error || 'Error asignando GLN')
  }
}

// Referencias
const warehouseForm = ref(null)
const zoneForm = ref(null)
const locationForm = ref(null)
const barcodeCanvas = ref(null)
const previewBarcodeCanvas = ref(null)

// Estado
const activeTab = ref('bodegas')
const loading = ref(false)
// const generatingBarcodes = ref(false) // Comentado - ya no se usa el botón Generar Códigos
const selectedWarehouseId = ref(null)
const selectedWarehouse = ref(null)
const isEditingWarehouse = ref(false)
const isEditingZone = ref(false)
const isEditingLocation = ref(false)
const showWarehouseDialog = ref(false)
const showZoneDialog = ref(false)
const showLocationDialog = ref(false)
const showBarcodePreviewDialog = ref(false)
const showPrintDialog = ref(false)
const showDeleteDialog = ref(false)
const deleteType = ref(null) // 'warehouse', 'zone', o 'location'
const deleteItem = ref(null)
const deleteConfirmMessage = ref('')

// Estado para ubicaciones
const selectedWarehouseIdForLocations = ref(null)
const selectedZoneIdForLocations = ref(null)
const selectedZoneForLocations = ref(null)
const zonesForLocations = ref([])
const locations = ref([])
const selectedLocations = ref([])
const selectedLocationForBarcode = ref(null)

// Estado para conteos de ubicaciones por zona
const locationCounts = ref({})

// Configuración de impresión
const printSettings = ref({
  printerType: 'standard',
  labelsPerRow: 2,
  labelSize: 'medium',
  labelFormat: '4x2',
  dpi: 203
})

const warehouseFormData = ref({
  code: '',
  name: '',
  address: '',
  description: '',
  status: 'Activo'
})

const zoneFormData = ref({
  code: '',
  name: '',
  description: '',
  weight: 1,
  aisle: '',
  rack: '',
  level: '',
  position: '',
  capacity: 1,
  warehouse_id: null
})

const locationFormData = ref({
  code: '',
  description: '',
  location_type: 'storage',
  aisle: '',
  rack: '',
  level: '',
  position: '',
  weight: 1,
  capacity: 1,
  width: null,
  length: null,
  height: null,
  zone_id: null,
  barcode: ''
})

// Configuración de ubicaciones
const showConfigDialog = ref(false)
const configForm = ref(null)
const locationConfig = ref({
  occupancyColors: {
    green: { max: 60 },
    yellow: { max: 85 },
    red: { min: 86 }
  },
  volumetricCapacity: {
    enabled: false
  }
})

// Computed
const warehouses = computed(() => store.state.warehouses.warehouses)
const zones = computed(() => store.state.zones.zones)

const isWarehouseFormValid = computed(() => {
  return warehouseFormData.value.code && warehouseFormData.value.name
})

const isZoneFormValid = computed(() => {
  return zoneFormData.value.code && 
         zoneFormData.value.name && 
         zoneFormData.value.weight > 0 && 
         zoneFormData.value.capacity > 0
})

const isLocationFormValid = computed(() => {
  const baseValid = locationFormData.value.code && 
                   locationFormData.value.location_type &&
                   locationFormData.value.weight > 0
  
  if (locationConfig.value.volumetricCapacity.enabled) {
    return baseValid && 
           locationFormData.value.width > 0 && 
           locationFormData.value.length > 0 && 
           locationFormData.value.height > 0
  } else {
    return baseValid && locationFormData.value.capacity > 0
  }
})

const selectedLocationsForPrint = computed(() => {
  return locations.value.filter(location => selectedLocations.value.includes(location.id))
})

// Watchers para generar códigos de barras en la vista previa
watch(showPrintDialog, async (newValue) => {
  if (newValue) {
    await nextTick()
    await generatePreviewBarcodes()
  }
})

watch(selectedLocationsForPrint, async () => {
  if (showPrintDialog.value) {
    await nextTick()
    await generatePreviewBarcodes()
  }
})

// Métodos
const loadData = async () => {
  try {
    loading.value = true
    await Promise.all([
      store.dispatch('warehouses/fetchWarehouses'),
      store.dispatch('zones/fetchZones')
    ])
  } catch (error) {
    console.error('Error loading data:', error)
    store.dispatch('showError', 'Error al cargar los datos')
  } finally {
    loading.value = false
  }
}

const addWarehouse = () => {
  isEditingWarehouse.value = false
  warehouseFormData.value = {
    code: '',
    name: '',
    address: '',
    description: '',
    status: 'Activo'
  }
  showWarehouseDialog.value = true
}

const editWarehouse = (warehouse) => {
  isEditingWarehouse.value = true
  warehouseFormData.value = { ...warehouse }
  showWarehouseDialog.value = true
}

const saveWarehouse = async () => {
  try {
    const { valid } = await warehouseForm.value.validate()
    if (!valid) return

    const action = isEditingWarehouse.value ? 'updateWarehouse' : 'createWarehouse'
    await store.dispatch(`warehouses/${action}`, warehouseFormData.value)
    
    showWarehouseDialog.value = false
    await loadData()
    store.dispatch('showSuccess', `Bodega ${isEditingWarehouse.value ? 'actualizada' : 'creada'} correctamente`)
  } catch (error) {
    console.error('Error saving warehouse:', error)
    store.dispatch('showError', `Error al ${isEditingWarehouse.value ? 'actualizar' : 'crear'} la bodega`)
  }
}

const addZone = (warehouseId) => {
  isEditingZone.value = false
  zoneFormData.value = {
    code: '',
    name: '',
    description: '',
    weight: 1,
    aisle: '',
    rack: '',
    level: '',
    position: '',
    capacity: 1,
    warehouse_id: warehouseId
  }
  showZoneDialog.value = true
}

const editZone = (zone) => {
  isEditingZone.value = true
  zoneFormData.value = { ...zone }
  showZoneDialog.value = true
}

const saveZone = async () => {
  try {
    const { valid } = await zoneForm.value.validate()
    if (!valid) return

    const action = isEditingZone.value ? 'updateZone' : 'createZone'
    const payload = { ...zoneFormData.value }
    
    await store.dispatch(`zones/${action}`, payload)
    showZoneDialog.value = false
    await loadWarehouseZones()
    store.dispatch('showSuccess', `Zona ${isEditingZone.value ? 'actualizada' : 'creada'} correctamente`)
  } catch (error) {
    console.error('Error saving zone:', error)
    store.dispatch('showError', `Error al ${isEditingZone.value ? 'actualizar' : 'crear'} la zona`)
  }
}

const deleteWarehouse = (warehouse) => {
  deleteType.value = 'warehouse'
  deleteItem.value = warehouse
  deleteConfirmMessage.value = `¿Está seguro que desea eliminar la bodega "${warehouse.name}"? Esta acción no se puede deshacer.`
  showDeleteDialog.value = true
}

const deleteZone = (zone) => {
  deleteType.value = 'zone'
  deleteItem.value = zone
  deleteConfirmMessage.value = `¿Está seguro que desea eliminar la zona "${zone.name}"? Esta acción no se puede deshacer.`
  showDeleteDialog.value = true
}

const confirmDelete = async () => {
  try {
    if (deleteType.value === 'warehouse') {
      await store.dispatch('warehouses/deleteWarehouse', deleteItem.value.id)
      store.dispatch('showSuccess', 'Bodega eliminada correctamente')
      // Recargar solo las bodegas, limpiar zona seleccionada
      await store.dispatch('warehouses/fetchWarehouses')
      selectedWarehouse.value = null
      selectedWarehouseId.value = null
    } else if (deleteType.value === 'zone') {
      await store.dispatch('zones/deleteZone', { 
        id: deleteItem.value.id, 
        warehouseId: deleteItem.value.warehouse_id 
      })
      store.dispatch('showSuccess', 'Zona eliminada correctamente')
      // El store ya recarga automáticamente las zonas de la bodega
      // Solo necesitamos recargar los conteos de ubicaciones
      await loadLocationCounts()
    } else if (deleteType.value === 'location') {
      await api.deleteLocation(deleteItem.value.id)
      store.dispatch('showSuccess', 'Ubicación eliminada correctamente')
      await loadZoneLocations()
      // Actualizar conteos de ubicaciones después de eliminar
      await loadLocationCounts()
    }
    showDeleteDialog.value = false
  } catch (error) {
    console.error('Error deleting item:', error)
    store.dispatch('showError', `Error al eliminar ${deleteType.value === 'warehouse' ? 'la bodega' : deleteType.value === 'zone' ? 'la zona' : 'la ubicación'}`)
  }
}

const loadWarehouseZones = async () => {
  if (!selectedWarehouseId.value || selectedWarehouseId.value === '') {
    selectedWarehouse.value = null
    return
  }
  
  selectedWarehouse.value = warehouses.value.find(w => w.id === parseInt(selectedWarehouseId.value))
  if (selectedWarehouse.value) {
    try {
      loading.value = true
      await store.dispatch('zones/fetchZonesByWarehouse', parseInt(selectedWarehouseId.value))
      // Cargar conteos de ubicaciones después de cargar las zonas
      await loadLocationCounts()
    } catch (error) {
      console.error('Error loading warehouse zones:', error)
      store.dispatch('showError', 'Error al cargar las zonas de la bodega')
    } finally {
      loading.value = false
    }
  }
}

const viewWarehouseZones = (warehouse) => {
  selectedWarehouse.value = warehouse
  selectedWarehouseId.value = warehouse.id
  activeTab.value = 'zonas'
  loadWarehouseZones()
}

const viewZoneLocations = (zone) => {
  selectedWarehouseIdForLocations.value = zone.warehouse_id
  selectedZoneIdForLocations.value = zone.id
  selectedZoneForLocations.value = zone
  activeTab.value = 'ubicaciones'
  loadWarehouseZonesForLocations()
  loadZoneLocations()
}

// Métodos para ubicaciones
const loadWarehouseZonesForLocations = async () => {
  // Limpiar estado inmediatamente al inicio para evitar mostrar datos antiguos
  zonesForLocations.value = []
  selectedZoneIdForLocations.value = null
  selectedZoneForLocations.value = null
  locations.value = []
  
  if (!selectedWarehouseIdForLocations.value) {
    return
  }
  
  try {
    console.log('🔄 Cargando zonas para warehouse ID:', selectedWarehouseIdForLocations.value)
    const response = await api.getWarehouseZones(selectedWarehouseIdForLocations.value)
    
    // Verificar que la respuesta es válida y es un array
    if (response.data && Array.isArray(response.data)) {
      zonesForLocations.value = response.data
      console.log('✅ Zonas cargadas:', response.data.length, 'zonas para warehouse', selectedWarehouseIdForLocations.value)
      
      // Log detallado de las zonas
      response.data.forEach(zone => {
        console.log(`   - ${zone.code} (${zone.name}) - Warehouse: ${zone.warehouse_id}`)
      })
    } else {
      console.warn('⚠️ Respuesta del API no válida:', response)
      zonesForLocations.value = []
    }
  } catch (error) {
    console.error('❌ Error loading zones for locations:', error)
    store.dispatch('showError', 'Error al cargar las zonas')
    zonesForLocations.value = []
  }
}

const loadZoneLocations = async () => {
  if (!selectedZoneIdForLocations.value) {
    locations.value = []
    selectedZoneForLocations.value = null
    return
  }
  
  selectedZoneForLocations.value = zonesForLocations.value.find(z => z.id === selectedZoneIdForLocations.value)
  
  try {
    loading.value = true
    const response = await api.getLocationsByZone(selectedZoneIdForLocations.value)
    locations.value = response.data
    selectedLocations.value = []
    await loadLocationCounts()
  } catch (error) {
    console.error('Error loading zone locations:', error)
    store.dispatch('showError', 'Error al cargar las ubicaciones')
  } finally {
    loading.value = false
  }
}

const addLocation = (zoneId) => {
  isEditingLocation.value = false
  locationFormData.value = {
    code: '',
    description: '',
    location_type: 'storage',
    aisle: '',
    rack: '',
    level: '',
    position: '',
    weight: 1,
    capacity: 1,
    width: null,
    length: null,
    height: null,
    zone_id: zoneId,
    barcode: ''
  }
  showLocationDialog.value = true
}

const editLocation = (location) => {
  isEditingLocation.value = true
  locationFormData.value = { ...location }
  showLocationDialog.value = true
  
  // Generar código de barras en el próximo tick
  nextTick(() => {
    if (location.barcode && barcodeCanvas.value) {
      generateBarcodeCanvas(location.barcode, barcodeCanvas.value)
    }
  })
}

const saveLocation = async () => {
  try {
    const { valid } = await locationForm.value.validate()
    if (!valid) return

    const payload = { ...locationFormData.value }
    
    if (isEditingLocation.value) {
      await api.updateLocation(payload.id, payload)
    } else {
      await api.createLocation(payload)
    }
    
    showLocationDialog.value = false
    await loadZoneLocations()
    // Actualizar conteos de ubicaciones después de crear/editar
    await loadLocationCounts()
    store.dispatch('showSuccess', `Ubicación ${isEditingLocation.value ? 'actualizada' : 'creada'} correctamente`)
  } catch (error) {
    console.error('Error saving location:', error)
    store.dispatch('showError', `Error al ${isEditingLocation.value ? 'actualizar' : 'crear'} la ubicación`)
  }
}

const deleteLocation = (location) => {
  deleteType.value = 'location'
  deleteItem.value = location
  deleteConfirmMessage.value = `¿Está seguro que desea eliminar la ubicación "${location.code}"? Esta acción no se puede deshacer.`
  showDeleteDialog.value = true
}

const getLocationCountForZone = (zoneId) => {
  return locationCounts.value[zoneId] || 0
}

// Función para cargar conteos de ubicaciones para las zonas actuales
const loadLocationCounts = async () => {
  if (!zones.value || zones.value.length === 0) return
  
  try {
    const promises = zones.value.map(async (zone) => {
      try {
        const response = await api.getLocationsByZone(zone.id)
        return { zoneId: zone.id, count: response.data.length }
      } catch (error) {
        console.error(`Error loading locations for zone ${zone.id}:`, error)
        return { zoneId: zone.id, count: 0 }
      }
    })
    
    const results = await Promise.all(promises)
    
    // Actualizar el estado reactivo
    const newCounts = {}
    results.forEach(result => {
      newCounts[result.zoneId] = result.count
    })
    locationCounts.value = newCounts
    
    console.log('📍 Conteos de ubicaciones cargados:', newCounts)
  } catch (error) {
    console.error('Error loading location counts:', error)
  }
}

const getLocationOccupancy = (location) => {
  // Priorizar cálculo volumétrico si está disponible
  if (location.is_volumetric && location.volumetric_capacity && location.volumetric_capacity > 0) {
    const volumeUsage = location.current_volume_usage || 0
    return Math.min(100, (volumeUsage / location.volumetric_capacity) * 100)
  }
  
  // Usar usage_percentage del backend si está disponible
  if (location.usage_percentage !== undefined) {
    return location.usage_percentage
  }
  
  // Fallback: calcular manualmente si no viene en la respuesta
  if (location.capacity && location.capacity > 0 && location.current_usage !== undefined) {
    return Math.min(100, (location.current_usage / location.capacity) * 100)
  }
  
  // Si no hay datos suficientes, retornar 0
  return 0
}

const getOccupancyColor = (occupancy) => {
  if (occupancy <= locationConfig.value.occupancyColors.green.max) return 'success'
  if (occupancy <= locationConfig.value.occupancyColors.yellow.max) return 'warning'
  return 'error'
}

// Métodos para códigos de barras
const showBarcodePreview = (location) => {
  selectedLocationForBarcode.value = location
  showBarcodePreviewDialog.value = true
  
  nextTick(() => {
    if (location.barcode && previewBarcodeCanvas.value) {
      generateBarcodeCanvas(location.barcode, previewBarcodeCanvas.value)
    }
  })
}

const generateBarcodeCanvas = (barcode, canvas) => {
  if (!canvas || !barcode) return
  
  try {
    JsBarcode(canvas, barcode, {
      format: "CODE128",
      width: 2,
      height: 100,
      displayValue: true,
      fontSize: 14,
      margin: 10
    })
  } catch (error) {
    console.error('Error generating barcode:', error)
  }
}

// Métodos de impresión
const printSingleBarcode = (location) => {
  selectedLocations.value = [location.id]
  showBarcodePreviewDialog.value = false
  showPrintDialog.value = true
}

// Función auxiliar para generar código de barras como imagen base64
const generateBarcodeImage = (barcodeText, width = 2, height = 100) => {
  console.log('🔄 Generando código de barras para:', barcodeText, 'width:', width, 'height:', height)
  return new Promise((resolve) => {
    const canvas = document.createElement('canvas')
    try {
      JsBarcode(canvas, barcodeText, {
        format: "CODE128",
        width: width,
        height: height,
        displayValue: true,
        fontSize: 14,
        margin: 10
      })
      // Usar setTimeout para asegurar que el canvas esté completamente renderizado
      setTimeout(() => {
        const dataURL = canvas.toDataURL('image/png')
        console.log('✅ Código de barras generado para:', barcodeText, 'Tamaño:', dataURL.length, 'caracteres')
        resolve(dataURL)
      }, 100)
    } catch (error) {
      console.error('❌ Error generating barcode image:', error)
      resolve('')
    }
  })
}

const printSelectedBarcodes = async () => {
  try {
    console.log('🖨️ Iniciando proceso de impresión...')
    const selectedLocationsList = selectedLocationsForPrint.value
    console.log('📋 Ubicaciones seleccionadas:', selectedLocationsList.length)
    
    // Pre-generar todas las imágenes de códigos de barras de forma asíncrona
    console.log('🔄 Generando imágenes de códigos de barras...')
    const locationsWithBarcodes = await Promise.all(
      selectedLocationsList.map(async (location, index) => {
        console.log(`📄 Procesando ubicación ${index + 1}/${selectedLocationsList.length}:`, location.code)
        const barcodeHeight = printSettings.value.labelSize === 'small' ? 40 : 
                             printSettings.value.labelSize === 'medium' ? 60 : 80
        const barcodeWidth = printSettings.value.printerType === 'label' ? 2 : 1
        
        const barcodeImage = await generateBarcodeImage(location.barcode, barcodeWidth, barcodeHeight)
        
        console.log(`✅ Imagen generada para ${location.code}:`, barcodeImage ? 'OK' : 'FALLO')
        
        return {
          ...location,
          barcodeImage: barcodeImage
        }
      })
    )
    
    console.log('✅ Todas las imágenes generadas. Total:', locationsWithBarcodes.length)
    console.log('📊 Resumen de imágenes:', locationsWithBarcodes.map(l => ({ 
      code: l.code, 
      hasImage: !!l.barcodeImage,
      imageSize: l.barcodeImage ? l.barcodeImage.length : 0
    })))
    
    let printContent = ''
    
    if (printSettings.value.printerType === 'standard') {
      console.log('📄 Generando contenido estándar...')
      printContent = generateStandardPrintContentWithImages(locationsWithBarcodes)
    } else {
      console.log('🏷️ Generando contenido de etiquetas...')
      printContent = generateLabelPrintContentWithImages(locationsWithBarcodes)
    }
    
    console.log('📝 Contenido HTML generado. Tamaño:', printContent.length, 'caracteres')
    console.log('🔍 Verificando si el HTML contiene imágenes:', printContent.includes('data:image/png'))
    
    // Método alternativo: crear un blob y abrir en nueva pestaña
    console.log('🌐 Creando blob para impresión...')
    const blob = new Blob([printContent], { type: 'text/html' })
    const blobUrl = URL.createObjectURL(blob)
    
    console.log('🌐 Abriendo ventana de impresión con blob URL...')
    const printWindow = window.open(blobUrl, '_blank')
    
    // Esperar a que la ventana se cargue completamente
    await new Promise(resolve => {
      const checkLoaded = () => {
        if (printWindow.document.readyState === 'complete') {
          console.log('✅ Ventana cargada completamente')
          resolve()
        } else {
          setTimeout(checkLoaded, 100)
        }
      }
      checkLoaded()
    })
    
    // Esperar un momento adicional para que las imágenes se rendericen
    await new Promise(resolve => setTimeout(resolve, 500))
    
    console.log('🖨️ Enviando a imprimir...')
    printWindow.print()
    
    // Limpiar el blob URL después de un tiempo
    setTimeout(() => {
      URL.revokeObjectURL(blobUrl)
      console.log('🧹 Blob URL limpiado')
    }, 5000)
    
    showPrintDialog.value = false
    console.log('✅ Proceso de impresión completado')
  } catch (error) {
    console.error('❌ Error printing barcodes:', error)
    store.dispatch('showError', 'Error al imprimir códigos de barras')
  }
}

const generateStandardPrintContentWithImages = (locationsList) => {
  console.log('📄 Generando contenido estándar para', locationsList.length, 'ubicaciones')
  const labelsPerRow = printSettings.value.labelsPerRow
  const labelSize = printSettings.value.labelSize
  
  let sizeClass = ''
  switch (labelSize) {
    case 'small': 
      sizeClass = 'width: 2cm; height: 1.5cm; font-size: 8px;'
      break
    case 'medium': 
      sizeClass = 'width: 4cm; height: 2.5cm; font-size: 10px;'
      break
    case 'large': 
      sizeClass = 'width: 6cm; height: 3.5cm; font-size: 12px;'
      break
  }
  
  let content = `
    <html>
      <head>
        <title>Códigos de Barras - Ubicaciones</title>
        <style>
          body { font-family: Arial, sans-serif; margin: 0; padding: 20px; }
          .print-grid { display: grid; grid-template-columns: repeat(${labelsPerRow}, 1fr); gap: 10px; }
          .barcode-label { 
            ${sizeClass}
            border: 1px solid #ccc; 
            padding: 5px; 
            text-align: center; 
            display: flex; 
            flex-direction: column; 
            justify-content: center;
            page-break-inside: avoid;
          }
          .barcode-code { font-weight: bold; margin-bottom: 2px; }
          .barcode-image { max-width: 100%; height: auto; margin: 2px 0; }
          .barcode-text { font-family: monospace; font-size: 0.7em; color: #666; }
          @media print {
            body { margin: 0; }
            .barcode-label { border: 1px solid #000; }
          }
        </style>
      </head>
      <body>
        <div class="print-grid">
  `
  
  locationsList.forEach((location, index) => {
    console.log(`📄 Procesando ubicación ${index + 1}: ${location.code}, tiene imagen:`, !!location.barcodeImage)
    content += `
      <div class="barcode-label">
        <div class="barcode-code">${location.code}</div>
        ${location.barcodeImage ? `<img src="${location.barcodeImage}" class="barcode-image" alt="${location.barcode}">` : '<!-- Sin imagen -->'}
        <div class="barcode-text">${location.barcode}</div>
      </div>
    `
  })
  
  content += `
        </div>
      </body>
    </html>
  `
  
  console.log('📄 Contenido estándar generado. Contiene imágenes:', content.includes('data:image/png'))
  return content
}

const generateLabelPrintContentWithImages = (locationsList) => {
  console.log('🏷️ Generando contenido de etiquetas para', locationsList.length, 'ubicaciones')
  // Para navegadores, mostrar una versión HTML con códigos de barras
  let content = `
    <html>
      <head>
        <title>Códigos de Barras - Etiquetas</title>
        <style>
          body { font-family: Arial, sans-serif; margin: 0; padding: 0; }
          .label { 
            width: ${printSettings.value.labelFormat === '2x1' ? '2in' : printSettings.value.labelFormat === '4x2' ? '4in' : '4in'}; 
            height: ${printSettings.value.labelFormat === '2x1' ? '1in' : printSettings.value.labelFormat === '4x2' ? '2in' : '6in'}; 
            border: 1px solid #000; 
            margin: 5px; 
            padding: 10px; 
            text-align: center;
            display: inline-block;
            page-break-inside: avoid;
          }
          .barcode-code { font-weight: bold; font-size: 14px; margin-bottom: 5px; }
          .barcode-image { max-width: 90%; height: auto; margin: 5px 0; }
          .barcode-text { font-family: monospace; font-size: 10px; color: #666; }
        </style>
      </head>
      <body>
  `
  
  locationsList.forEach((location, index) => {
    console.log(`🏷️ Procesando etiqueta ${index + 1}: ${location.code}, tiene imagen:`, !!location.barcodeImage)
    content += `
      <div class="label">
        <div class="barcode-code">${location.code}</div>
        ${location.barcodeImage ? `<img src="${location.barcodeImage}" class="barcode-image" alt="${location.barcode}">` : '<!-- Sin imagen -->'}
        <div class="barcode-text">${location.barcode}</div>
      </div>
    `
  })
  
  content += `
      </body>
    </html>
  `
  
  console.log('🏷️ Contenido de etiquetas generado. Contiene imágenes:', content.includes('data:image/png'))
  return content
}

// Métodos de configuración
const getOccupancyColorPreview = (occupancy) => {
  if (occupancy <= locationConfig.value.occupancyColors.green.max) return 'success'
  if (occupancy <= locationConfig.value.occupancyColors.yellow.max) return 'warning'
  return 'error'
}

/* Método generateBarcodes comentado - ya no es necesario ya que los códigos se generan automáticamente
const generateBarcodes = async () => {
  try {
    generatingBarcodes.value = true
    const response = await api.generateLocationBarcodes()
    
    if (response.data.success) {
      store.dispatch('showSuccess', response.data.message)
      // Recargar las ubicaciones para mostrar los nuevos códigos
      await loadZoneLocations()
    }
  } catch (error) {
    console.error('Error generating barcodes:', error)
    store.dispatch('showError', 'Error al generar códigos de barras')
  } finally {
    generatingBarcodes.value = false
  }
}
*/

const generatePreviewBarcodes = async () => {
  await nextTick()
  selectedLocationsForPrint.value.forEach((location, index) => {
    const canvasId = `previewCanvas${index}`
    const canvas = document.getElementById(canvasId)
    
    if (canvas && location.barcode) {
      try {
        JsBarcode(canvas, location.barcode, {
          format: "CODE128",
          width: 1,
          height: 25,
          displayValue: false,
          margin: 2
        })
      } catch (error) {
        console.error('Error generating preview barcode:', error)
      }
    }
  })
}

const saveConfig = () => {
  // Guardar configuración en localStorage
  localStorage.setItem('locationConfig', JSON.stringify(locationConfig.value))
  showConfigDialog.value = false
  store.dispatch('showSuccess', 'Configuración guardada correctamente')
}

const cancelConfig = () => {
  // Restaurar configuración desde localStorage o valores por defecto
  loadConfig()
  showConfigDialog.value = false
}

const loadConfig = () => {
  const savedConfig = localStorage.getItem('locationConfig')
  if (savedConfig) {
    try {
      const parsed = JSON.parse(savedConfig)
      locationConfig.value = {
        ...locationConfig.value,
        ...parsed
      }
    } catch (error) {
      console.error('Error loading config:', error)
    }
  }
}

// Método para calcular capacidad volumétrica
const calculateVolumetricCapacity = () => {
  if (locationFormData.value.width && locationFormData.value.length && locationFormData.value.height) {
    return locationFormData.value.width * locationFormData.value.length * locationFormData.value.height
  }
  return 0
}

// Cargar datos al montar el componente
loadConfig()
loadData()
</script>

<style scoped>
.instalaciones-container {
  padding: 20px;
}

/* Estilos para las tablas de instalaciones */
.warehouses-table,
.zones-table,
.locations-table {
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  background: white !important;
}

.warehouses-table .v-data-table__thead,
.zones-table .v-data-table__thead,
.locations-table .v-data-table__thead {
  background-color: #f8f9fa !important;
}

.warehouses-table .v-data-table__thead .v-data-table__th,
.zones-table .v-data-table__thead .v-data-table__th,
.locations-table .v-data-table__thead .v-data-table__th {
  background-color: #f8f9fa !important;
  font-weight: 600 !important;
  color: #495057 !important;
  border-bottom: 2px solid #dee2e6 !important;
  padding: 12px 16px !important;
  font-size: 0.875rem !important;
}

.warehouses-table .v-data-table__tbody .v-data-table__td,
.zones-table .v-data-table__tbody .v-data-table__td,
.locations-table .v-data-table__tbody .v-data-table__td {
  padding: 12px 16px !important;
  border-bottom: 1px solid #dee2e6 !important;
}

.warehouses-table .v-data-table__tbody tr:hover,
.zones-table .v-data-table__tbody tr:hover,
.locations-table .v-data-table__tbody tr:hover {
  background-color: #f8f9fa !important;
}

/* Estilos para códigos de barras */
.barcode-display {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 10px;
  background-color: #f8f9fa;
  border-radius: 4px;
}

.barcode-canvas {
  max-width: 100%;
  height: auto;
  border: 1px solid #dee2e6;
  background-color: white;
}

.preview-barcode-canvas {
  max-width: 100%;
  height: 25px;
  background-color: white;
  border: 1px solid #ddd;
  margin: 2px 0;
}

/* Estilos para vista previa de impresión */
.print-preview {
  border: 1px solid #dee2e6;
  border-radius: 4px;
  padding: 15px;
  background-color: #f8f9fa;
  max-height: 200px;
  overflow-y: auto;
}

.print-preview.standard {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
  gap: 10px;
}

.print-preview.label {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}

.barcode-label {
  border: 1px solid #ccc;
  padding: 8px;
  text-align: center;
  background-color: white;
  border-radius: 2px;
}

.barcode-label.small {
  width: 80px;
  height: 40px;
  font-size: 8px;
}

.barcode-label.medium {
  width: 120px;
  height: 60px;
  font-size: 10px;
}

.barcode-label.large {
  width: 160px;
  height: 80px;
  font-size: 12px;
}

.barcode-code {
  font-weight: bold;
  margin-bottom: 2px;
}

.barcode-text {
  font-family: monospace;
  font-size: 0.8em;
  color: #666;
}

/* Responsive Headers */
.locations-header {
  display: flex;
  flex-direction: column;
  gap: 12px;
  align-items: flex-start;
}

@media (min-width: 768px) {
  .locations-header {
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
    gap: 16px;
  }
}

.locations-title {
  flex: 1;
}

.locations-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;
}

@media (min-width: 768px) {
  .locations-actions {
    gap: 12px;
  }
}

/* Responsive Design */
@media (max-width: 768px) {
  .instalaciones-container {
    padding: 10px;
  }
  
  .v-tabs .v-tab {
    font-size: 0.875rem !important;
    padding: 0 12px !important;
  }
  
  .text-h6 {
    font-size: 1.1rem !important;
  }
  
  /* Filtros responsivos */
  .v-select {
    margin-bottom: 8px;
  }
  
  .v-select .v-field__input {
    font-size: 14px !important;
    padding: 8px 12px !important;
  }
  
  /* Botones responsivos */
  .v-btn {
    min-width: 44px !important;
  }
  
  .v-btn .v-btn__content {
    font-size: 0.875rem !important;
  }
}

@media (max-width: 480px) {
  .instalaciones-container {
    padding: 8px;
  }
  
  .text-h6 {
    font-size: 1rem !important;
  }
  
  .v-tabs .v-tab {
    font-size: 0.75rem !important;
    padding: 0 8px !important;
  }
  
  .locations-actions {
    gap: 6px;
  }
  
  .v-btn {
    min-width: 40px !important;
    height: 36px !important;
  }
  
  .v-chip {
    font-size: 0.75rem !important;
  }
}

/* Estilos para gaps en flexbox */
.gap-2 {
  gap: 8px;
}

.badge {
  padding: 5px 10px;
  border-radius: 20px;
  font-weight: 500;
}

.badge-success {
  background-color: #2ecc71;
  color: #fff;
}

.badge-danger {
  background-color: #e74c3c;
  color: #fff;
}

.form-text {
  font-size: 0.8rem;
}

.instalaciones {
  padding: 20px;
}

.warehouses-grid {
  display: grid;
  gap: 20px;
}

@media (min-width: 768px) {
  .warehouses-grid {
    grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  }
}

/* Estilos responsivos para códigos de barras */
@media (max-width: 768px) {
  .barcode-canvas {
    max-width: 250px;
  }
  
  .print-preview.standard {
    grid-template-columns: repeat(auto-fit, minmax(80px, 1fr));
  }
  
  .barcode-label {
    font-size: 8px;
  }
}

/* Estilos para impresión */
@media print {
  .instalaciones-container {
    padding: 0;
  }
  
  .barcode-label {
    border: 1px solid #000 !important;
    page-break-inside: avoid;
  }
}
</style>
